/**
 * The public site header: brand, primary navigation, and the session actions.
 *
 * ── NO HAMBURGER, DELIBERATELY ──────────────────────────────────────────────
 * There are five things in this bar. At 320px they wrap onto two rows and
 * every one of them stays visible, reachable and 44px tall. A disclosure menu
 * would hide four links behind a button, and doing it correctly means an
 * `aria-expanded` state that is only meaningful below one breakpoint — a
 * control that lies to a screen reader on every wider viewport unless it is
 * driven by a media query in JS. Wrapping is the structurally honest answer at
 * this link count. If navigation grows past ~7 items, revisit it then and give
 * the menu a real `matchMedia` binding rather than a `sm:hidden` class.
 *
 * ── THE SKIP LINK ───────────────────────────────────────────────────────────
 * First focusable element in the document, visible on focus, pointing at
 * `#main`. `main.ts` had to learn to leave hash links alone for it to work —
 * its click handler used to swallow every same-origin anchor, which turned
 * "skip to content" into a full re-render that put focus back at the top.
 */
import { t } from '../i18n';
import type { AuthUser } from '../../core/state/auth-store';
import { TAP_TARGET, el, type Child } from './dom';
import { skinButton } from '../ui/button';
import { createSlotIcon } from '../ui/icon-registry';
import { attachPress } from '../ui/press';
import { ROUTES } from './routes';
import { brandHeroLockup } from './brand';
import { brandName, type GameSiteInfo } from './site-info';

export interface HeaderOptions {
  /** The signed-in player, or `null`. Decides which actions are offered. */
  readonly user: AuthUser | null;
  readonly siteInfo: GameSiteInfo;
  /** Path of the page being rendered, for `aria-current`. */
  readonly currentPath: string;
  /**
   * End the session. Omitted renders no sign-out control.
   *
   * A callback rather than an import of `logout()`: signing out is three things
   * (revoke the session, drop the websocket, route away), only one of which
   * this module can see, and the router owns the other two.
   */
  readonly onSignOut?: () => void;
}

const PRIMARY_LINK_CLASS = `${TAP_TARGET} px-2 text-text-dim hover:text-text hover:underline`;
const PRIMARY_LINK_CURRENT_CLASS = `${TAP_TARGET} px-2 text-text underline`;
const BUTTON_CLASS =
  `${TAP_TARGET} justify-center rounded px-4 font-bold bg-gold-bright text-ink hover:bg-sun`;
const GHOST_BUTTON_CLASS =
  `${TAP_TARGET} justify-center rounded border border-border px-4 text-text hover:border-gold`;

/**
 * The AAA skin + the press spring on a header CTA. The anchors keep their own
 * activation (the router intercepts the click); `attachPress` is here for the
 * squash-and-spring under the finger, so its onPress is deliberately empty.
 */
function pressable<T extends HTMLElement>(node: T, variant: 'primary' | 'secondary'): T {
  skinButton(node, { variant });
  attachPress(node, { onPress: () => undefined });
  return node;
}

export function createHeader(options: HeaderOptions): HTMLElement {
  const { user, siteInfo, currentPath, onSignOut } = options;

  /*
   * FOUR LINKS, AND THE GUIDE IS SECOND.
   *
   * The owner asked for the guide to be reachable from the landing header, and
   * this header IS the landing header — it is site chrome for every document
   * route (`hasSiteChrome` in `routes.ts`), so wiring it here puts it on `/`
   * and on the legal, FAQ and news pages at once.
   *
   * It sits directly after Home because it is what the visitor who has not
   * decided yet is looking for, and BEFORE News, which is for players who
   * already play. Four is still inside the no-hamburger budget above: at 320px
   * the row wraps and every link stays 44px tall and visible.
   */
  const navItems: Child[] = [
    navLink(ROUTES.home, t('nav.home'), currentPath),
    navLink(ROUTES.guide, t('nav.guide'), currentPath),
    navLink(ROUTES.news, t('nav.news'), currentPath),
    navLink(ROUTES.faq, t('nav.faq'), currentPath),
  ];

  /*
   * A SIGNED-IN PLAYER NEEDS A WAY BACK AND A WAY OUT. NOT A WAY IN.
   *
   * This header is the PUBLIC site chrome — the landing page, the legal
   * documents, the FAQ, the news. It is not the game, and it deliberately
   * exposes no game destination: settings live inside the shell at
   * `/settings`, reached from the game's own navigation, and putting a second
   * door to them out here made the marketing front door double as an app menu.
   * A player who wants their settings goes back into the game and opens them
   * there, which is the one place they are found.
   *
   * What stays is the pair that only this chrome can offer: the way back into
   * the game, and signing out — the latter a BUTTON, not a link, because it
   * changes server state and because that keeps the router's same-origin
   * anchor interceptor out of it.
   */
  const actions: Child[] = user
    ? [
        pressable(el('a', { class: BUTTON_CLASS, href: ROUTES.play }, [
          createSlotIcon(document, 'action-play', { size: 20, class: 'rga-btn__icon mr-1.5' }),
          el('span', { text: t('nav.back_to_game') }),
        ]), 'primary'),
        onSignOut === undefined ? null : signOutButton(onSignOut),
      ]
    : [
        pressable(el('a', { class: GHOST_BUTTON_CLASS, href: ROUTES.login, text: t('nav.sign_in') }), 'secondary'),
        pressable(el('a', { class: BUTTON_CLASS, href: ROUTES.register, text: t('nav.sign_up') }), 'primary'),
      ];

  return el('header', {
    // Sticky: the header stays put as the page scrolls under it (owner ask).
    // A translucent ground + blur so the content reads through it, and a high
    // z so it sits above the hero film and section art.
    class:
      'sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur ' +
      'ps-[max(1rem,env(safe-area-inset-left))] pe-[max(1rem,env(safe-area-inset-right))] py-1.5',
    'data-testid': 'site-header',
  }, [
    skipLink(),
    el('div', {
      class: 'mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-4 gap-y-2',
    }, [
      // THE LOCKUP: the full painted wordmark image (`/brand/lockup.png`), owner
      // ask — the header shows the logo art, not the mark-plus-text pairing.
      // `brandHeroLockup()` owns the asset path and emits the named <img>; it is
      // sized for a header here (height-capped, auto width) rather than the
      // hero's large `max-w`.
      //
      // The image goes straight into this existing anchor: the anchor already
      // carries the tap target, the focus ring and the route home.
      //
      // This header is site chrome for every DOCUMENT route (`hasSiteChrome`
      // in `routes.ts`), which is why wiring it here — and only here — puts the
      // logo on the landing page AND on all six auth screens at once.
      el('a', {
        class: `${TAP_TARGET} flex items-center`,
        href: ROUTES.home,
        'aria-label': brandName(siteInfo),
      }, [brandHeroLockup({ name: brandName(siteInfo), class: 'h-12 w-auto sm:h-16' })]),
      el('nav', { class: 'order-3 w-full sm:order-none sm:w-auto', 'aria-label': t('nav.primary_label') }, [
        el('ul', { class: 'flex flex-wrap items-center gap-x-1' }, navItems),
      ]),
      el('div', { class: 'ms-auto flex flex-wrap items-center gap-2' }, actions),
    ]),
  ]);
}

/**
 * `aria-current="page"` rather than a class alone: the fact that a link points
 * at the page you are on is information, and a colour change does not carry it
 * to anyone using a screen reader.
 */
function navLink(href: string, label: string, currentPath: string): HTMLElement {
  const here = current(currentPath, href);
  return el('li', {}, [
    el('a', {
      class: here ? PRIMARY_LINK_CURRENT_CLASS : PRIMARY_LINK_CLASS,
      href,
      text: label,
      'aria-current': here ? 'page' : false,
    }),
  ]);
}

/**
 * Is this link the page we are on? `/news` is also "current" while reading
 * `/news/patch-3` — a reader who has drilled into an article is still in the
 * news section, and saying otherwise leaves the nav with nothing marked.
 * `/` is exact, or it would match everything.
 */
function current(currentPath: string, href: string): boolean {
  if (currentPath === href) return true;
  return href !== ROUTES.home && currentPath.startsWith(`${href}/`);
}

/**
 * Signing out is a state change on the server, so it is a `<button>` — not a
 * link styled as one. That also keeps it clear of the router's same-origin
 * anchor interceptor, which would otherwise have to learn about it.
 */
function signOutButton(onSignOut: () => void): HTMLElement {
  const button = el('button', {
    type: 'button',
    class: GHOST_BUTTON_CLASS,
    'data-testid': 'header-sign-out',
  }, [
    createSlotIcon(document, 'action-sign-out', { size: 20, class: 'rga-btn__icon mr-1.5' }),
    el('span', { text: t('nav.sign_out') }),
  ]);
  skinButton(button, { variant: 'secondary' });
  attachPress(button, { onPress: onSignOut });
  return button;
}

function skipLink(): HTMLElement {
  return el('a', {
    href: '#main',
    class:
      'sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 ' +
      `focus:rounded focus:bg-surface focus:px-4 focus:text-text ${TAP_TARGET}`,
    text: t('common.skip_to_content'),
  });
}
